import { useEffect, useState } from 'react'
import {
  Creator,
  CreatorOnboardingStep,
  MutationUpdateCreatorArgs,
  MutationSignContractArgs,
  ContractStatus,
} from '../../../gql/types'
import { AnalyticsEvent, trackEvent } from '../../../utils/analytics'
import {
  Button,
  ButtonColor,
  Loader,
  NotificationType,
} from '../../../components'
import { useMutation, useQuery } from '@apollo/client'
import { UpdateCreatorResponse, UPDATE_CREATOR } from '../../../gql'
import {
  SignContractResponse,
  SIGN_CONTRACT,
  GetActiveContractTemplateResponse,
  GET_ACTIVE_CONTRACT_TEMPLATE,
} from '../../../gql/contracts'
import { useNotification } from '../../../utils/hooks'
import { Steps } from '../components/Steps'
import { Bullets } from '../components/Bullets'
import { collectBrowserFingerprint } from '../../../utils/browserFingerprint'

interface Props {
  creator: Creator
}

const SetAgreeTerms = ({ creator }: Props) => {
  const { addNotification } = useNotification()
  const [hasAgreed, setHasAgreed] = useState(false)

  const {
    data: contractData,
    loading: contractLoading,
    error: contractError,
  } = useQuery<GetActiveContractTemplateResponse>(
    GET_ACTIVE_CONTRACT_TEMPLATE,
    {
      variables: {
        options: {
          filters: {
            statuses: [ContractStatus.ACTIVE],
          },
        },
      },
    },
  )

  const [updateCreator, { loading: updateLoading }] = useMutation<
    UpdateCreatorResponse,
    MutationUpdateCreatorArgs
  >(UPDATE_CREATOR)

  const [signContract, { loading: signLoading }] = useMutation<
    SignContractResponse,
    MutationSignContractArgs
  >(SIGN_CONTRACT)

  const loading = updateLoading || signLoading

  useEffect(() => {
    trackEvent(AnalyticsEvent.viewCreatorsOnboardingSetAgreeTerms)
  }, [])

  const goBack = async () => {
    try {
      await updateCreator({
        variables: {
          id: creator.id,
          input: {
            onboarding: CreatorOnboardingStep.setSocialMedia,
          },
        },
      })
    } catch (error) {
      addNotification(error.message, NotificationType.error)
    }
  }

  const onSubmit = async () => {
    if (!hasAgreed) {
      addNotification(
        'Please read and accept the terms before continuing.',
        NotificationType.error,
      )
      return
    }

    try {
      // Collect browser fingerprint data for legal compliance
      const browserData = collectBrowserFingerprint()

      if (contractData?.getActiveCreatorContractTemplate) {
        // Sign the contract with all necessary data
        await signContract({
          variables: {
            creatorId: creator.id,
            contractTemplateId:
              contractData?.getActiveCreatorContractTemplate?.id,
            input: {
              ...browserData, // All the browser fingerprint data
            },
          },
        })
      }

      // Update creator onboarding status
      await updateCreator({
        variables: {
          id: creator.id,
          input: {
            onboarding: CreatorOnboardingStep.completed,
          },
        },
      })

      trackEvent(AnalyticsEvent.creatorAppliedToClip)
      addNotification('Contract signed successfully!', NotificationType.success)
    } catch (error) {
      console.error('@error SetAgreeTerms onSubmit', error)
      addNotification(
        error.message || 'Failed to sign contract. Please try again.',
        NotificationType.error,
      )
    }
  }

  if (contractLoading) {
    return <Loader />
  }

  if (contractError) {
    console.log(contractError)
    return <p>There has been an error. Try refreshing the page</p>
  }

  const activeContract = contractData?.getActiveCreatorContractTemplate

  return (
    <div className='min-h-screen flex flex-col'>
      <div className='flex-grow'>
        <Steps
          creator={creator}
          stepCurrent={CreatorOnboardingStep.agreeTerms}
        />

        <div className='min-h-full flex flex-col justify-center pt-4 px-4 md:mt-8 lg:px-8'>
          <div className='sm:mx-auto sm:w-full sm:max-w-2xl'>
            <div className='md:shadow rounded-t-md sm:overflow-hidden mb-4'>
              <div className='py-5 md:bg-white space-y-6 sm:p-6'>
                <div className='sm:mx-auto'>
                  {!activeContract && (
                    <div>
                      <h2 className='mt-0 text-left text-3xl font-extrabold text-gray-900'>
                        Agree Terms
                      </h2>
                      <p className='mt-2 text-gray-700 text-sm font-bold'>
                        To collaborate with brands on Clip please agree the
                        following
                      </p>
                      <p className='mt-8 text-left text-sm text-gray-600'>
                        1. Upon receipt of the product, the creator shall have a
                        maximum of three (3) days to create, deliver, and upload
                        the agreed-upon content to Clip.
                      </p>
                      <p className='mt-8 text-left text-sm text-gray-600'>
                        2. Content Rights. The brand retains the right to use
                        the content created by you for marketing and promotional
                        purposes, including but not limited to ads, social
                        media, websites, and other online platforms. You grant
                        the brand a perpetual, non-exclusive, royalty-free
                        license to use the content for these purposes.
                      </p>
                    </div>
                  )}
                  {/* Contract Text */}
                  {activeContract && (
                    <div>
                      <div style={{ margin: '0 0 12px' }}>
                        <h2 className='mt-0 text-left text-3xl font-extrabold text-gray-900'>
                          Agree Terms
                        </h2>
                        <p className='mt-2 text-gray-700 text-sm font-bold'>
                          This is a plain-English summary - see{' '}
                          <a
                            href={activeContract.link}
                            target='_blank'
                            rel='noopener noreferrer'
                            className='text-blue-600 hover:text-blue-800 underline'
                          >
                            full contract
                          </a>{' '}
                          for details.
                        </p>
                      </div>

                      <div className='borderrounded-lg overflow-y-auto'>
                        <div
                          className='prose prose-sm max-w-none text-gray-700'
                          dangerouslySetInnerHTML={{
                            __html: activeContract.text,
                          }}
                        />
                      </div>

                      <p
                        style={{
                          margin: '12px 0 0',
                          fontSize: '.8rem',
                          color: '#6b7280',
                        }}
                      >
                        This summary is not a substitute for the contract. If
                        anything here conflicts with the full terms, the full
                        terms control.
                      </p>

                      {activeContract.link && (
                        <p className='mt-2 text-sm text-gray-600'>
                          <a
                            href={activeContract.link}
                            target='_blank'
                            rel='noopener noreferrer'
                            className='text-blue-600 hover:text-blue-800 underline'
                          >
                            View full terms document →
                          </a>
                        </p>
                      )}
                    </div>
                  )}
                </div>

                {/* Acceptance Checkbox */}
                <div className='mb-8'>
                  <label className='flex items-start space-x-3 cursor-pointer'>
                    <input
                      type='checkbox'
                      checked={hasAgreed}
                      onChange={(e) => setHasAgreed(e.target.checked)}
                      className='mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded'
                    />
                    <div className='text-sm'>
                      <span className='text-gray-900 font-medium'>
                        I have read, understood, and agree to the Terms of
                        Service
                      </span>
                      <p className='text-gray-600 mt-1'>
                        By checking this box, you confirm that you accept all
                        terms and conditions outlined above.
                      </p>
                    </div>
                  </label>
                </div>

                {/* Legal Notice */}
                <div className='bg-yellow-50 border border-yellow-200 rounded-md p-4 mb-8'>
                  <div className='flex'>
                    <div className='flex-shrink-0'>
                      <svg
                        className='h-5 w-5 text-yellow-400'
                        viewBox='0 0 20 20'
                        fill='currentColor'
                      >
                        <path
                          fillRule='evenodd'
                          d='M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z'
                          clipRule='evenodd'
                        />
                      </svg>
                    </div>
                    <div className='ml-3'>
                      <h3 className='text-sm font-medium text-yellow-800'>
                        Legal Information
                      </h3>
                      <div className='mt-2 text-sm text-yellow-700'>
                        <p>
                          Your IP address, browser information, and other
                          technical details will be recorded for legal
                          compliance and security purposes when you sign this
                          agreement.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className='flex justify-between md:shadow rounded-b-md py-3 md:bg-gray-50 text-right sm:px-6'>
                <div></div>
                <div className='flex gap-3'>
                  <Button
                    title='Go back'
                    type='button'
                    colour={ButtonColor.white}
                    loading={loading}
                    disabled={loading}
                    onClick={goBack}
                    className='mt-4'
                  />

                  <Button
                    title='Agree Terms'
                    type='submit'
                    className='mt-4'
                    onClick={onSubmit}
                    loading={loading}
                    disabled={!hasAgreed || loading}
                  />
                </div>
              </div>
            </div>
            <div className='text-sm text-gray-600 text-center mb-8'>
              <p>
                <strong>Need help?</strong> Contact our support team at{' '}
                <a
                  href='mailto:hello@useclip.com'
                  className='text-blue-600 hover:text-blue-800'
                >
                  hello@useclip.com
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>

      <Bullets stepCurrent={CreatorOnboardingStep.agreeTerms} />
    </div>
  )
}

export { SetAgreeTerms }
